import React, { useEffect, useMemo, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import { ArrowRight, CheckCircle2, KeyRound, Loader2, Server, ShieldCheck } from "lucide-react";

import { deploymentModes, getDeploymentMode, isLocalApplianceOrigin, isLocalEditionBuild } from "../../services/deploymentConfig";
import { localActivationService, normalizeActivationHospital } from "../../services/localActivationService";

const isLocalMode = (mode) => mode === deploymentModes.OFFLINE || mode === deploymentModes.HYBRID;

const ClearHealthLogo = ({ inverted = false, className = "" }) => {
  return (
    <div className={`inline-flex items-center gap-3 ${className}`}>
      <img
        src="/logo.svg"
        alt=""
        className="h-9 w-9 shrink-0"
      />
      <span className={`text-xl font-semibold tracking-tight ${inverted ? "text-white" : "text-slate-900"}`}>ClearHealth</span>
    </div>
  );
};

const LocalActivation = () => {
  const history = useHistory();
  const forceReactivate = new URLSearchParams(window.location.search || "").get("reactivate") === "1";
  const [activationCode, setActivationCode] = useState("");
  const [status, setStatus] = useState(null);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const mode = getDeploymentMode();
  const localShell = isLocalApplianceOrigin() || isLocalEditionBuild() || isLocalMode(mode);
  const hospital = useMemo(() => normalizeActivationHospital(status || {}), [status]);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      if (!localShell) {
        setIsLoading(false);
        return;
      }
      try {
        const result = await localActivationService.status();
        if (!mounted) return;
        setStatus(result);
        if (result?.activated && !forceReactivate) {
          sessionStorage.setItem("hospitalId", result.hospitalId || "");
          sessionStorage.setItem("hospitalDetails", JSON.stringify(normalizeActivationHospital(result)));
        }
      } catch (err) {
        if (!mounted) return;
        setError(err?.message || "Local API is unavailable. Start the ClearHealth Local Server and try again.");
      } finally {
        if (mounted) setIsLoading(false);
      }
    };
    load();
    return () => {
      mounted = false;
    };
  }, [forceReactivate, localShell]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");
    setIsSubmitting(true);
    try {
      const result = await localActivationService.activateByCode({
        activationCode,
      });
      const activated = normalizeActivationHospital(result?.hospital || result?.hospitalProfile || {});
      const nextStatus = {
        activated: true,
        ...activated,
        hospitalId: activated.hospitalId || result?.hospital?.hospitalId || "",
        deploymentMode: activated.deploymentMode || result?.hospital?.deploymentMode,
        localServerStatus: activated.localServerStatus || result?.hospital?.localServerStatus,
        licenseStatus: activated.licenseStatus || result?.hospital?.licenseStatus,
        syncEnabled: activated.syncEnabled ?? result?.hospital?.syncEnabled,
        localApiEnabled: activated.localApiEnabled ?? result?.hospital?.localApiEnabled,
        serverId: activated.serverId || result?.hospital?.serverId,
        lastActivationAt: activated.lastActivationAt || result?.hospital?.lastActivationAt,
      };
      setStatus(nextStatus);
      sessionStorage.setItem("hospitalId", nextStatus.hospitalId || "");
      sessionStorage.setItem("hospitalDetails", JSON.stringify(normalizeActivationHospital(nextStatus)));
      setActivationCode("");
    } catch (err) {
      setError(err?.message || "Activation failed. Check the code and try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const displayActivated = status?.activated && !forceReactivate;
  const environmentLabel = status?.deploymentMode === deploymentModes.HYBRID ? "Hybrid Local Server" : "Local Edition";
  const progressSteps = [
    ["Local API", error && !status?.activated ? "Check required" : "Ready"],
    ["Activation code", activationCode ? "Entered" : displayActivated ? "Applied" : "Waiting"],
    ["Developer Console", isSubmitting ? "Verifying" : displayActivated ? "Verified" : "Pending"],
    ["Hospital profile", displayActivated ? "Saved" : "Pending"],
    ["License/modules", displayActivated ? "Loaded" : "Pending"],
    ["Login", displayActivated ? "Ready" : "Pending"],
  ];

  if (!localShell) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 px-6">
        <div className="w-full max-w-lg rounded-2xl border border-slate-200 bg-white p-8 text-center shadow-sm">
          <ClearHealthLogo />
          <h1 className="mt-6 text-2xl font-semibold text-slate-900">Online Cloud deployment</h1>
          <p className="mt-3 text-sm leading-6 text-slate-600">
            Local server activation is only required for ClearHealth Local Edition and Hybrid Local Server installs.
          </p>
          <Link className="mt-6 inline-flex items-center justify-center rounded-xl bg-primary px-4 py-3 text-sm font-semibold text-white" to="/login">
            Go to Login
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[linear-gradient(180deg,#edf4fb_0%,#f7fbff_48%,#ffffff_100%)] px-6 py-10">
      <div className="mx-auto flex min-h-[calc(100vh-5rem)] max-w-5xl items-center justify-center">
        <div className="grid w-full overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-[0_25px_70px_rgba(15,23,42,0.10)] lg:grid-cols-[0.9fr_1.1fr]">
          <div className="bg-primary px-8 py-10 text-white sm:px-10">
            <ClearHealthLogo inverted />
            <div className="mt-12 inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em]">
              <Server className="h-3.5 w-3.5" />
              Local Server
            </div>
            <h1 className="mt-5 text-3xl font-semibold tracking-tight">Activate ClearHealth Local Server</h1>
            <p className="mt-4 text-sm leading-6 text-blue-50">
              Connect this server to your hospital profile. Enter the activation code provided during hospital onboarding.
            </p>
           
            <div className="mt-5 rounded-2xl border border-white/15 bg-white/10 p-4 text-sm">
              <p className="font-semibold">Activation progress</p>
              <ol className="mt-3 space-y-2">
                {progressSteps.map(([label, state], index) => (
                  <li key={label} className="flex items-center justify-between gap-3 text-blue-50">
                    <span>{index + 1}. {label}</span>
                    <span className="rounded-full bg-white/15 px-2 py-0.5 text-[11px] font-semibold text-white">{state}</span>
                  </li>
                ))}
              </ol>
            </div>
          </div>

          <div className="p-8 sm:p-10">
            <div className="mb-8 flex items-center justify-between gap-4">
              <div>
                <div className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-primary">
                  <ShieldCheck className="h-3.5 w-3.5" />
                  {displayActivated ? environmentLabel : forceReactivate ? "Reactivation Mode" : "Activation Required"}
                </div>
                <h2 className="mt-4 text-2xl font-semibold text-slate-900">
                  {displayActivated ? "Server Activated Successfully" : forceReactivate ? "Reactivate Server" : "Enter Activation Code"}
                </h2>
              </div>
            </div>

            {isLoading ? (
              <div className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600">
                <Loader2 className="h-5 w-5 animate-spin text-primary" />
                Checking local server activation...
              </div>
            ) : displayActivated ? (
              <div className="space-y-5">
                <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-5">
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="mt-0.5 h-5 w-5 text-emerald-600" />
                    <div>
                      <p className="font-semibold text-emerald-900">Connected to: {hospital.hospitalName}</p>
                      <p className="mt-1 text-sm text-emerald-700">Mode: {environmentLabel}</p>
                    </div>
                  </div>
                </div>
                <dl className="grid gap-3 rounded-2xl border border-slate-200 p-5 text-sm sm:grid-cols-2">
                  <div>
                    <dt className="text-slate-500">Hospital ID</dt>
                    <dd className="font-mono font-semibold text-slate-900">{status.hospitalId || "-"}</dd>
                  </div>
                  <div>
                    <dt className="text-slate-500">License</dt>
                    <dd className="font-semibold text-slate-900">{status.licenseStatus || "-"}</dd>
                  </div>
                  <div>
                    <dt className="text-slate-500">Sync</dt>
                    <dd className="font-semibold text-slate-900">{status.syncEnabled ? "Enabled" : "Disabled"}</dd>
                  </div>
                  <div>
                    <dt className="text-slate-500">Server</dt>
                    <dd className="font-mono text-slate-900">{status.serverId || "-"}</dd>
                  </div>
                </dl>
                <button
                  type="button"
                  className="inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-primary px-4 py-3.5 text-sm font-semibold text-white transition hover:bg-primary_light"
                  onClick={() => history.push("/login")}
                >
                  Login to ClearHealth
                  <ArrowRight className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  className="inline-flex w-full items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
                  onClick={() => history.push("/server-management")}
                >
                  Open Server Management
                </button>
              </div>
            ) : (
              <form className="space-y-5" onSubmit={handleSubmit}>
                <div>
                  <label htmlFor="activationCode" className="mb-2 block text-sm font-medium text-slate-700">
                    Activation Code
                  </label>
                  <div className="relative">
                    <KeyRound className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
                    <input
                      id="activationCode"
                      required
                      autoComplete="one-time-code"
                      className="block w-full rounded-2xl border border-slate-200 bg-white py-3 pl-12 pr-4 font-mono text-sm uppercase text-slate-800 outline-none transition focus:border-primary focus:ring-4 focus:ring-primary/10"
                      placeholder="Enter Activation Code"
                      value={activationCode}
                      onChange={(event) => setActivationCode(event.target.value.trim())}
                      disabled={isSubmitting}
                    />
                  </div>
                </div>

                {error ? (
                  <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">
                    {error}
                  </div>
                ) : null}

                <button
                  type="submit"
                  className="inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-primary px-4 py-3.5 text-sm font-semibold text-white transition hover:bg-primary_light disabled:cursor-not-allowed disabled:opacity-70"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
                  {isSubmitting ? "Activating..." : "Activate Server"}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LocalActivation;
